// ============================================================================
// Common Types
// ============================================================================

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// ============================================================================
// Admin Metrics (Overview Page)
// ============================================================================

export interface AdminMetricsResponse {
  // User metrics
  total_users: number;
  active_users_30_days: number;
  new_users_today: number;
  new_users_week: number;

  // Load metrics
  total_loads: number;
  loads_in_progress: number;
  loads_completed_week: number;

  // Financial metrics (in cents)
  total_revenue_cents: number;
  total_expenses_cents: number;
  outstanding_invoice_cents: number;
  overdue_invoice_cents: number;
  invoices_paid_week: number;
  invoices_overdue: number;

  // Receipt metrics
  receipts_pending: number;
  receipts_processed: number;
  receipts_failed: number;

  // Timestamp
  generated_at: string;
}

// ============================================================================
// User Types (Admin View)
// ============================================================================

export interface AdminUserResponse {
  id: string;
  cognito_sub: string;
  email: string;
  name: string;
  phone?: string;
  status: 'active' | 'suspended';
  email_verified: boolean;
  created_at: string;
  updated_at: string;
  last_active_at?: string;

  // Business info (summarized)
  has_business: boolean;
  company_name?: string;
  mc_number?: string;
  dot_number?: string;
  ifta_base_state?: string;

  // Stats
  load_count: number;
  expense_count: number;
  invoice_count: number;
}

export interface AdminUserListResponse {
  users: AdminUserResponse[];
  total_count: number;
  limit: number;
  offset: number;
}

export interface UserFilter {
  search?: string;
  has_business?: boolean;
  sort?: 'created_at_desc' | 'created_at_asc' | 'name_asc' | 'name_desc';
  limit?: number;
  offset?: number;
}

// Derive status from user data (for display purposes)
export type UserStatus = 'active' | 'inactive' | 'new';

export function getUserStatus(user: AdminUserResponse): UserStatus {
  const now = new Date();
  const createdAt = new Date(user.created_at);
  const daysSinceCreated = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);

  if (daysSinceCreated <= 7) {
    return 'new';
  }

  // Consider active if they have any data
  if (user.load_count > 0 || user.expense_count > 0) {
    return 'active';
  }

  return 'inactive';
}

// ============================================================================
// Platform Health Types
// ============================================================================

export interface AdminHealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  components: AdminComponentHealth[];
  checked_at: string;
}

export interface AdminComponentHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  latency_ms: number;
  message?: string;
  last_checked: string;
}

// ============================================================================
// Audit Log Types
// ============================================================================

export type AuditAction =
  | 'user.created'
  | 'user.updated'
  | 'user.login'
  | 'business.updated'
  | 'load.created'
  | 'load.updated'
  | 'load.deleted'
  | 'load.status_change'
  | 'expense.created'
  | 'expense.updated'
  | 'expense.deleted'
  | 'invoice.created'
  | 'invoice.sent'
  | 'invoice.paid'
  | 'receipt.uploaded'
  | 'receipt.processed'
  | 'receipt.failed'
  | 'admin.user_view'
  | 'admin.user_status_change';

export interface AuditLogEntry {
  id: string;
  user_id?: string;
  user_email: string;
  action: string; // Using string to allow for new actions not in the enum
  target_type?: string;
  target_id?: string;
  details?: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  request_id?: string;
  created_at: string;
}

export interface AuditLogListResponse {
  entries: AuditLogEntry[];
  total_count: number;
  limit: number;
  offset: number;
}

export interface AuditLogFilter {
  search?: string;
  user_id?: string;
  action?: string[];
  target_type?: string;
  target_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Type Guards
// ============================================================================

export function isApiError(response: unknown): response is ApiError {
  return (
    typeof response === 'object' &&
    response !== null &&
    'error' in response &&
    typeof (response as ApiError).error === 'object'
  );
}
